import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MessageCircle, Send, Loader2, CheckCircle2 } from "lucide-react";
import { SafeHtml } from "@/components/SafeHtml";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogComments({ slug, postTitle }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorName, setAuthorName] = useState("");
  const [authorEmail, setAuthorEmail] = useState("");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/public/blog/${slug}/comments`);
      setComments(res.data.comments || []);
    } catch (_) {}
    setLoading(false);
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!authorName.trim() || !content.trim()) {
      setError("Podaj imię i treść komentarza");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/blog/${slug}/comments`, {
        author_name: authorName.trim(),
        author_email: authorEmail.trim() || undefined,
        content: content.trim(),
      });
      setSubmitted(true);
      setContent("");
      setTimeout(() => setSubmitted(false), 5000);
    } catch (e) {
      setError(e.response?.data?.detail || "Nie udało się wysłać");
    }
    setSubmitting(false);
  };

  // Schema.org Comment markup
  const commentsSchema = comments.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": postTitle,
    "comment": comments.map(c => ({
      "@type": "Comment",
      "author": { "@type": "Person", "name": c.author_name },
      "datePublished": c.created_at,
      "text": (c.content || "").replace(/<[^>]+>/g, " ").substring(0, 500),
    })),
    "commentCount": comments.length,
  } : null;

  return (
    <div className="mt-12 pt-8 border-t border-slate-100">
      {commentsSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(commentsSchema) }}
        />
      )}

      <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
        <MessageCircle className="w-5 h-5 text-blue-600" />
        Komentarze {comments.length > 0 && <span className="text-sm text-slate-500 font-normal">({comments.length})</span>}
      </h3>

      {/* Lista komentarzy */}
      {loading ? (
        <div className="text-center py-8 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-slate-500 text-sm italic mb-6">
          Brak komentarzy — bądź pierwszy!
        </p>
      ) : (
        <div className="space-y-5 mb-8">
          {comments.map((c) => (
            <article
              key={c.id}
              className={`flex gap-4 p-4 rounded-xl border ${
                c.ai_persona === "redakcja"
                  ? "bg-blue-50/50 border-blue-100"
                  : "bg-white border-slate-100 hover:border-slate-200"
              } transition-colors`}
            >
              <img
                src={c.author_avatar}
                alt={c.author_name}
                loading="lazy"
                className="w-10 h-10 rounded-full shrink-0 bg-slate-100"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-semibold text-slate-900 text-sm">{c.author_name}</span>
                  {c.is_ai && c.ai_persona === "redakcja" && (
                    <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      Redakcja
                    </span>
                  )}
                  <time className="text-xs text-slate-400" dateTime={c.created_at}>
                    {c.created_at ? new Date(c.created_at).toLocaleDateString("pl-PL", { day: "numeric", month: "short" }) : ""}
                  </time>
                </div>
                <SafeHtml
                  className="prose prose-sm max-w-none text-slate-700 [&_p]:my-1 [&_strong]:text-slate-900"
                  html={c.content}
                />
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Form dodawania komentarza */}
      <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
        <h4 className="font-bold text-slate-900 mb-1">Dodaj komentarz</h4>
        <p className="text-sm text-slate-500 mb-4">
          Komentarz pojawi się po zatwierdzeniu przez redakcję.
        </p>

        {submitted ? (
          <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <CheckCircle2 className="w-5 h-5 shrink-0" />
            <span className="text-sm">Dziękujemy! Komentarz został przesłany do moderacji.</span>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Imię *"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                maxLength={100}
                required
                className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              />
              <input
                type="email"
                placeholder="Email (opcjonalny, nie publikujemy)"
                value={authorEmail}
                onChange={(e) => setAuthorEmail(e.target.value)}
                maxLength={200}
                className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <textarea
              placeholder="Treść komentarza... *"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={5000}
              required
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 resize-y"
            />
            {error && (
              <p className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">{error}</p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white px-5 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? "Wysyłam..." : "Wyślij komentarz"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
